// @flow
import AppVersion from 'app/common/lib/AppVersion';
import Csrf from 'app/common/lib/Csrf';
import urlUtil from 'app/common/lib/urlUtil';

type HttpRequestMethod = 'GET' | 'POST' | 'PUT' | 'OPTIONS' | 'HEAD' | 'TRACE';

type Config = {
  async: boolean,
  cache: boolean,
  complete: (XMLHttpRequest, string, Config) => void,
  data: ?Object | string,
  dataType: 'json',
  error: (XMLHttpRequest, string, string | null, Config) => void,
  headers: { [string]: string },
  method?: HttpRequestMethod,
  query: { [string]: string },
  success: (XMLHttpRequest, any, Config) => void,
  timeout: number,
  type: HttpRequestMethod,
  url: string,
};

type Options = {
  async?: boolean,
  cache?: boolean,
  complete?: (XMLHttpRequest, string, Config) => void,
  data?: Object,
  dataType?: 'json',
  error?: (XMLHttpRequest, string, string | null, Config) => void,
  headers?: { [string]: string },
  query?: { [string]: string },
  success?: (XMLHttpRequest, any, Config) => void,
  timeout?: number,
  type?: HttpRequestMethod,
  url: string,
};

const noop = (...args) => undefined;
const isSuccess = (status) => (status >= 200 && status < 300) || status === 304;

const manageData = (config: Config) => {
  if (config.data && typeof config.data === 'object') {
    if (config.type === 'GET') {
      config.url = urlUtil.appendQueryString(config.url, config.data);
      config.data = undefined;
    } else {
      config.data = urlUtil.getQueryStringFromObject(config.data);
    }
  }
};

const manageHeaders = (xhr: XMLHttpRequest, config: Config) => {
  xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
  xhr.setRequestHeader('Accept', 'application/json, text/javascript, */*, q=0.01');
  xhr.setRequestHeader('X-APP-VERSION', AppVersion.currentVersion);

  if (config.method !== 'GET' && config.data) {
    xhr.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded');
  }

  if (Csrf._needsCsrfToken(config.type, config.url)) {
    xhr.setRequestHeader('X-CSRFToken', Csrf.getCSRFToken());
  }

  Object.keys(config.headers).forEach((headerName) => {
    xhr.setRequestHeader(headerName, config.headers[headerName]);
  });
};

const onreadystatechange = (xhr, config: Config, abortTimeout?: number) => () => {
  if (xhr.readyState === 4) {
    xhr.onreadystatechange = noop;
    // $FlowFixMe: new errors found from flow 0.65 upgrade
    clearTimeout(abortTimeout);

    if (isSuccess(xhr.status)) {
      try {
        const result = /^\s*$/.test(xhr.responseText) ? null : JSON.parse(xhr.responseText);
        config.success(xhr, result, config);
        config.complete(xhr, 'success', config);
      } catch (e) {
        config.error(xhr, 'parsererror', e.message, config);
        config.complete(xhr, 'parsererror', config);
      }
    } else {
      const status = xhr.status ? 'error' : 'abort';
      config.error(xhr, status, xhr.statusText || null, config);
      config.complete(xhr, status, config);
    }
  }
};

const defaults = (options: Options) => {
  const config: Config = {
    async: true,
    cache: false,
    complete: (xhr, status, config) => undefined,
    data: undefined,
    dataType: 'json',
    error: (xhr, status, error, config) => undefined,
    headers: {},
    query: {},
    success: (xhr, response, config) => undefined,
    timeout: 0,
    type: 'GET',
    ...options,
  };
  return config;
};

const request = (config: Config) => {
  const xhr = new XMLHttpRequest();

  // Build timeout callback
  const abortTimeout = config.timeout
    ? setTimeout(() => {
        xhr.onreadystatechange = noop;
        xhr.abort();
        config.error(xhr, 'timeout', null, config);
      }, config.timeout)
    : undefined;

  // Attach state change listener
  // $FlowFixMe: new errors found from flow 0.65 upgrade
  xhr.onreadystatechange = onreadystatechange(xhr, config, abortTimeout);
  // Manage query params and form data
  manageData(config);
  // Add cache busting query param
  if (config.cache === false && config.type === 'GET') {
    config.url = urlUtil.appendQueryString(config.url, { _: Date.now() });
  }
  // Open the request
  xhr.open(config.type, config.url, config.async);
  // Append all the required headers
  manageHeaders(xhr, config);
  // Fire off the request
  xhr.send(config.data || null);
  return xhr;
};

export default function ajax(options: Options) {
  return request(defaults(options));
}
